// MOBA-style kill-streak banners (top-centre, fade quickly). The server decides
// the tier (KillEvent.streak) so every client agrees; this is pure presentation.
// Announcements QUEUE rather than replace: if several land together they play in
// arrival order, one after another.

import { type KillEvent } from "../../shared/protocol/index.js";
import { $, escapeHtml } from "../dom.js";
import { state } from "../state.js";

import { playSfx } from "../audio/index.js";

/** tier code (KillEvent.streak) → banner label + CSS class. Enemy multikills
 *  (1-5) get flashier; team-kill betrayals (6-8) get grim, horror styling. */
const TIERS: Record<number, { label: string; cls: string; sfx?: string }> = {
  1: { label: "FIRST BLOOD!", cls: "fb", sfx: "first_blood" },
  2: { label: "DOUBLE KILL!", cls: "t2", sfx: "double_kill" },
  3: { label: "TRIPLE KILL!!", cls: "t3", sfx: "triple_kill" },
  4: { label: "MANIAC!!", cls: "t4", sfx: "maniac" },
  5: { label: "SAVAGE!!!", cls: "t5", sfx: "savage" },
  6: { label: "BETRAYAL", cls: "b1" },
  7: { label: "TRAITOR", cls: "b2" },
  8: { label: "KINSLAYER", cls: "b3" },
};

const DISPLAY_MS = 1500; // each queued banner holds this long before the next plays

interface Item {
  tier: number;
  killer: number;
  mult: number;
}
const queue: Item[] = [];
let playTimer: ReturnType<typeof setTimeout> | null = null;
let playing = false;

/** Clear the queue and the on-screen banner (call on match/round start). */
export function resetAnnouncements(): void {
  queue.length = 0;
  if (playTimer) clearTimeout(playTimer);
  playTimer = null;
  playing = false;
  const el = $("announce");
  el.className = "announce";
  el.innerHTML = "";
}

/** Queue a kill event's announcement (no-op when the server set no tier). */
export function announceKill(e: KillEvent): void {
  if (!e.streak || !TIERS[e.streak]) return;
  queue.push({ tier: e.streak, killer: e.killer, mult: e.mult });
  if (!playing) playNext();
}

function playNext(): void {
  const item = queue.shift();
  if (!item) {
    playing = false;
    return;
  }
  playing = true;
  show(item);
  playTimer = setTimeout(playNext, DISPLAY_MS);
}

function show(item: Item): void {
  const t = TIERS[item.tier];
  const r = state.roster.get(item.killer);
  const el = $("announce");
  el.className = "announce";
  void el.offsetWidth; // force reflow so the pop replays even back-to-back
  el.className = `announce show ${t.cls}`;
  // Succession multiplier (savage/kinslayer repeats) sits small to the right.
  const mult = item.mult >= 2 ? `<span class="ann-mult">×${item.mult}</span>` : "";
  el.innerHTML =
    `<div class="ann-label">${escapeHtml(t.label)}${mult}</div>` +
    `<div class="ann-by" style="color:${r?.color ?? "#ddd"}">${escapeHtml(r?.name ?? "?")}</div>`;
  
  if (t.sfx) {
    playSfx(t.sfx, 0.9);
  }
}
